import { useEffect, useState } from "react";

import { cn } from "@/web/lib/styling";

interface TimeUnits {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calculateTimeRemaining(endTimestamp: number): TimeUnits | null {
  const now = Date.now();
  const difference = endTimestamp - now;

  if (difference <= 0) return null;

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((difference % (1000 * 60)) / 1000),
  };
}

function AnimatedNumber({
  value,
  label,
  className,
}: {
  value: number;
  label?: string;
  className?: string;
}) {
  const [displayValue, setDisplayValue] = useState(value);
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    if (displayValue !== value) {
      setIsChanging(true);
      const timeout = setTimeout(() => {
        setDisplayValue(value);
        setIsChanging(false);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [value, displayValue]);

  return (
    <div
      className={cn(
        "rounded-[5px] bg-background/15 p-[4px] pt-[6px] text-[11px] font-semibold transition-opacity duration-300",
        isChanging ? "opacity-0" : "opacity-100",
        className,
      )}
    >
      {String(displayValue).padStart(2, "0")}
      {label}
    </div>
  );
}

export function CardTimer({
  endTimestamp,
  className,
  innerClassName,
  includeSeconds = true,
}: {
  endTimestamp: number;
  className?: string;
  innerClassName?: string;
  includeSeconds?: boolean;
}) {
  const [timeRemaining, setTimeRemaining] = useState<TimeUnits | null>(() =>
    calculateTimeRemaining(endTimestamp),
  );

  useEffect(() => {
    setTimeRemaining(calculateTimeRemaining(endTimestamp));
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(endTimestamp));
    }, 1000);
    return () => clearInterval(interval);
  }, [endTimestamp]);

  if (timeRemaining === null) {
    return (
      <div className="flex items-center justify-center">
        <span className="font-medium">Finished</span>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <AnimatedNumber value={timeRemaining.days} label="d" className={innerClassName} />
      <span>:</span>
      <AnimatedNumber value={timeRemaining.hours} label="h" className={innerClassName} />
      <span>:</span>
      <AnimatedNumber value={timeRemaining.minutes} label="m" className={innerClassName} />
      {includeSeconds && (
        <>
          <span>:</span>
          <AnimatedNumber value={timeRemaining.seconds} label="s" className={innerClassName} />
        </>
      )}
    </div>
  );
}
